import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './TeacherEventDetailView.css';
import './TeacherDetailPages.css';

function TeacherEventDetailView({ event, onClose }) {
  const [classNames, setClassNames] = useState({});
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  const loadClassNames = useCallback(async () => {
    if (!event.participatingClassIds || event.participatingClassIds.length === 0) return;

    const names = {};
    for (const classId of event.participatingClassIds) {
      try {
        const classQuery = query(collection(db, 'classes'), where('__name__', '==', classId));
        const snapshot = await getDocs(classQuery);
        if (!snapshot.empty) {
          const classData = snapshot.docs[0].data();
          names[classId] = classData.name || classId;
        } else {
          names[classId] = classId;
        }
      } catch (error) {
        console.error('Error loading class name:', error);
        names[classId] = classId;
      }
    }
    setClassNames(names);
  }, [event]);

  const loadEventAttendance = useCallback(async () => {
    if (!event.id || !event.participatingClassIds || event.participatingClassIds.length === 0) return;

    try {
      const allRecords = [];
      for (const classId of event.participatingClassIds) {
        const attendanceQuery = query(
          collection(db, 'classes', classId, 'attendanceRecords'),
          where('eventId', '==', event.id)
        );
        const snapshot = await getDocs(attendanceQuery);
        snapshot.docs.forEach(doc => {
          allRecords.push({ id: doc.id, ...doc.data() });
        });
      }
      setAttendanceRecords(allRecords);
    } catch (error) {
      console.error('Error loading event attendance:', error);
    }
  }, [event]);

  useEffect(() => {
    loadClassNames();
    loadEventAttendance();
  }, [loadClassNames, loadEventAttendance]);

  const hasLocation = () => {
    return event.location && event.location.latitude && event.location.longitude &&
      event.location.latitude !== 0 && event.location.longitude !== 0;
  };

  const getMapUrl = () => {
    if (hasLocation()) {
      const { latitude, longitude } = event.location;
      return `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
    }
    if (event.location?.address) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(event.location.address)}&z=15&output=embed`;
    }
    return null;
  };

  const openDirections = () => {
    if (hasLocation()) {
      const { latitude, longitude } = event.location;
      window.open(`https://maps.google.com/?q=${latitude},${longitude}`, '_blank');
    } else if (event.location?.address) {
      window.open(`https://maps.google.com/?q=${encodeURIComponent(event.location.address)}`, '_blank');
    }
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'Marathon': '🏃',
      'Walkathon': '🚶',
      'Fun Run': '🏃',
      'Track and Field': '🏟️',
      'Field Trip': '🚌',
      'Assembly': '👥',
      'Sporting Event': '⚽',
      'Walk': '🚶',
      'Other': '📅'
    };
    return icons[eventType] || '📅';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return new Date(dateValue).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return new Date(dateValue).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const attendanceRate = attendanceRecords.length > 0
    ? (attendanceRecords.filter(r => r.status === 'Present').length / attendanceRecords.length * 100).toFixed(1)
    : 0;

  const mapUrl = getMapUrl();

  return (
    <div className="detail-page">
      <button className="detail-back-button" onClick={onClose}>◄ Back to Events</button>
      <div className="detail-hero">
        <div className="detail-avatar">{getEventIcon(event.eventType)}</div>
        <div className="detail-hero-info">
          <h2>{event.name}</h2>
          <p className="detail-subtitle">{formatDate(event.date)}</p>
          <div className="detail-badges">
            <span className="detail-badge">{event.eventType || 'Event'}</span>
            {event.status && (
              <span className={`detail-badge ${event.status?.toLowerCase() === 'cancelled' ? 'red' : 'green'}`}>
                {event.status}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="detail-grid">
          <div className="detail-card full-width">
            <h3>Event Details</h3>
            <div className="detail-row">
              <span className="detail-label">Name</span>
              <span className="detail-value">{event.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <span className="detail-value">{event.eventType}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className={`detail-value status ${event.status?.toLowerCase()}`}>
                {event.status}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date</span>
              <span className="detail-value">{formatDate(event.date)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Time</span>
              <span className="detail-value">{formatTime(event.date)}</span>
            </div>
            {event.description && (
              <div className="detail-row description">
                <span className="detail-label">Description</span>
                <span className="detail-value">{event.description}</span>
              </div>
            )}
          </div>

          {mapUrl && (
            <div className="detail-card full-width">
              <h3>Location</h3>
              <div className="location-info">
                <div className="location-icon">📍</div>
                <div className="location-address">
                  {event.location?.address || 'Event Location'}
                </div>
              </div>
              <div className="map-container">
                <iframe
                  src={mapUrl}
                  width="100%"
                  height="200"
                  style={{ border: 0, borderRadius: '10px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Event Location"
                />
              </div>
              <button className="directions-button" onClick={openDirections}>
                <span className="directions-icon">🗺️</span>
                <span>Get Directions</span>
              </button>
            </div>
          )}

          {event.participatingClassIds && event.participatingClassIds.length > 0 && (
            <div className="detail-card">
              <h3>Participating Classes</h3>
              <div className="classes-list">
                {event.participatingClassIds.map((classId, index) => (
                  <div key={index} className="class-tag">
                    {classNames[classId] || classId}
                  </div>
                ))}
              </div>
            </div>
          )}

          {attendanceRecords.length > 0 && (
            <div className="detail-card">
              <h3>Attendance Summary</h3>
              <div className="detail-row">
                <span className="detail-label">Attendance Rate</span>
                <span className="detail-value" style={{ color: '#14532d' }}>{attendanceRate}%</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Total Marked</span>
                <span className="detail-value">{attendanceRecords.length}</span>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

export default TeacherEventDetailView;
